import { BanknoteIcon, CreditCard, Landmark, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { Button, Money } from "@/components/ui";
import { parseAmountInput } from "@/lib/money";
import { changeFor, missingFor } from "@/lib/totals";

type Method = "efectivo" | "tarjeta" | "transferencia";

interface Payment {
  method: Method;
  amountCents: number;
  tipCents: number;
}

const METHODS: { id: Method; label: string; icon: typeof BanknoteIcon; key: string }[] = [
  { id: "efectivo", label: "Efectivo", icon: BanknoteIcon, key: "F1" },
  { id: "tarjeta", label: "Tarjeta", icon: CreditCard, key: "F2" },
  { id: "transferencia", label: "Transferencia", icon: Landmark, key: "F3" },
];

/** Billetes y monedas frecuentes, para cobrar en dos toques. */
const QUICK_CASH = [50_00, 100_00, 200_00, 500_00, 1000_00];

/**
 * Cobro con pagos mixtos.
 *
 * Pensado para teclado: se escribe el importe y se pulsa Enter para agregarlo.
 * `F1`/`F2`/`F3` cambian el método y `F4` confirma, que es la misma tecla que
 * abre este diálogo. El cajero no necesita soltar el teclado numérico.
 */
export function PaymentDialog({
  totalCents,
  onCancel,
  onConfirm,
}: {
  totalCents: number;
  onCancel: () => void;
  onConfirm: (payments: { method: string; amount_cents: number; tip_cents: number }[]) => void;
}) {
  const [method, setMethod] = useState<Method>("efectivo");
  const [amount, setAmount] = useState("");
  const [payments, setPayments] = useState<Payment[]>([]);
  const [tip, setTip] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const paid = useMemo(
    () => payments.reduce((sum, payment) => sum + payment.amountCents, 0),
    [payments],
  );
  const missing = missingFor(paid, totalCents);
  const change = changeFor(paid, totalCents);

  const typedCents = parseAmountInput(amount);
  // Lo que se agregaría si se pulsa Enter ahora mismo.
  const candidate = typedCents > 0 ? typedCents : missing;
  const cashPaid = payments
    .filter((payment) => payment.method === "efectivo")
    .reduce((sum, payment) => sum + payment.amountCents, 0);
  const candidateChange = changeFor(paid + candidate, totalCents);
  const changeIsCovered = candidateChange <= cashPaid + (method === "efectivo" ? candidate : 0);

  function addPayment(amountCents: number, currentMethod: Method = method) {
    if (amountCents <= 0) return;
    setPayments((current) => [
      ...current,
      { method: currentMethod, amountCents, tipCents: 0 },
    ]);
    setAmount("");
  }

  function confirm() {
    if (missing > 0 || !changeIsCovered || submitting) return;
    setSubmitting(true);
    const withTip: Payment[] = payments.map((payment, index) =>
      index === 0 ? { ...payment, tipCents: tip } : payment,
    );
    onConfirm(
      withTip.map((payment) => ({
        method: payment.method,
        amount_cents: payment.amountCents,
        tip_cents: payment.tipCents,
      })),
    );
  }

  // Atajos propios del diálogo. Se registran aquí y no en la pantalla para que
  // no compitan con los del POS mientras está abierto.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const key = event.key;
      if (key === "Escape") {
        event.preventDefault();
        onCancel();
        return;
      }
      if (key === "F1" || key === "F2" || key === "F3") {
        event.preventDefault();
        const target = METHODS[Number(key.slice(1)) - 1];
        if (target) setMethod(target.id);
        return;
      }
      if (key === "F4") {
        event.preventDefault();
        confirm();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <div className="w-full max-w-2xl space-y-5 rounded-msr-lg border border-line bg-snow p-6 text-carbon shadow-2xl">
        <header className="flex items-end justify-between">
          <div>
            <h2 className="font-display text-xl font-bold text-carbon">Cobrar</h2>
            <p className="text-xs text-muted">
              <kbd className="kbd">F1</kbd>–<kbd className="kbd">F3</kbd> método ·{" "}
              <kbd className="kbd">Enter</kbd> agregar · <kbd className="kbd">F4</kbd> confirmar ·{" "}
              <kbd className="kbd">Esc</kbd> cancelar
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs tracking-wide text-muted uppercase">Total</p>
            <Money cents={totalCents} size="lg" className="font-bold text-carbon" />
          </div>
        </header>

        <div className="grid gap-3 sm:grid-cols-3">
          {METHODS.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setMethod(item.id)}
                className={`flex items-center gap-2 rounded-msr border px-4 py-3 text-sm font-semibold transition-colors ${
                  method === item.id
                    ? "border-electric bg-electric/15 text-carbon"
                    : "border-line bg-surface/50 text-muted hover:bg-surface hover:text-carbon"
                }`}
              >
                <Icon aria-hidden className="size-4" />
                {item.label}
                <span className="ml-auto text-[10px] text-muted">{item.key}</span>
              </button>
            );
          })}
        </div>

        <div className="space-y-2">
          <label className="relative block">
            <span className="sr-only">Importe recibido</span>
            <input
              autoFocus
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  addPayment(candidate);
                }
              }}
              placeholder={missing > 0 ? (missing / 100).toFixed(2) : "0.00"}
              inputMode="decimal"
              className="focus:border-electric tabular h-14 w-full rounded-msr border border-line bg-snow px-4 text-right text-2xl font-bold text-carbon placeholder:text-muted focus:outline-none"
            />
          </label>

          <div className="flex flex-wrap gap-2">
            {method === "efectivo" ? (
              <>
                <SmallButton
                  onClick={() => addPayment(missing)}
                  disabled={missing === 0}
                >
                  Exacto <Money cents={missing} size="sm" />
                </SmallButton>
                {QUICK_CASH.filter((value) => value >= totalCents).slice(0, 4).map((value) => (
                  <SmallButton key={value} onClick={() => addPayment(value)}>
                    <Money cents={value} size="sm" />
                  </SmallButton>
                ))}
              </>
            ) : (
              <SmallButton onClick={() => addPayment(missing)} disabled={missing === 0}>
                Cobrar <Money cents={missing} size="sm" />
              </SmallButton>
            )}
          </div>
        </div>

        {payments.length > 0 ? (
          <ul className="space-y-1">
            {payments.map((payment, index) => (
              <li
                key={`${payment.method}-${index}`}
                className="flex items-center gap-3 rounded-msr border border-line bg-surface/40 px-3 py-2 text-sm text-carbon"
              >
                <span className="capitalize font-medium">{payment.method}</span>
                <Money cents={payment.amountCents} size="sm" className="ml-auto font-semibold" />
                <button
                  type="button"
                  aria-label={`Quitar pago de ${payment.method}`}
                  onClick={() =>
                    setPayments((current) => current.filter((_, position) => position !== index))
                  }
                  className="grid size-7 place-items-center rounded-md border border-line text-muted hover:bg-rose-500/10 hover:text-rose-500 hover:border-rose-500/30"
                >
                  <Trash2 aria-hidden className="size-3.5" />
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1 rounded-msr border border-line bg-surface/30 p-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted">Recibido</span>
              <Money cents={paid} size="sm" className="font-semibold text-carbon" />
            </div>
            <div className="flex justify-between">
              <span className="text-muted">{missing > 0 ? "Falta" : "Cambio"}</span>
              <Money
                cents={missing > 0 ? missing : change}
                size="sm"
                className={missing > 0 ? "text-warning font-semibold" : "text-success font-semibold"}
              />
            </div>
          </div>

          <div className="space-y-1 rounded-msr border border-line bg-surface/30 p-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted">Propina</span>
              <div className="flex gap-1">
                {[0, 10_00, 20_00].map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setTip(value)}
                    className={`rounded-md border px-2 py-1 text-xs transition-colors ${
                      tip === value ? "border-electric bg-electric/15 text-electric font-semibold" : "border-line bg-snow text-muted hover:bg-surface"
                    }`}
                  >
                    {value === 0 ? "Sin" : <Money cents={value} size="sm" />}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-[11px] text-muted">
              La propina no forma parte del total; entra aparte al cajón.
            </p>
          </div>
        </div>

        {!changeIsCovered ? (
          <p className="rounded-msr border border-warning/30 bg-warning/10 px-3 py-2 text-sm text-warning">
            El cambio sale del cajón: no puede superar lo pagado en efectivo.
          </p>
        ) : null}

        <div className="flex gap-3">
          <Button variant="secondary" size="lg" fullWidth onClick={onCancel}>
            Cancelar
          </Button>
          <Button
            size="lg"
            fullWidth
            disabled={missing > 0 || !changeIsCovered || submitting}
            loading={submitting}
            onClick={confirm}
            icon={<Plus aria-hidden className="size-4" />}
          >
            Cobrar <Money cents={totalCents} size="sm" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function SmallButton({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="rounded-msr border border-line bg-surface/50 px-3 py-2 text-sm text-carbon transition-colors hover:bg-surface disabled:opacity-40"
    >
      {children}
    </button>
  );
}
